import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ExternalLink, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import Layout from '../components/layout/Layout';
import SEO from '../components/shared/SEO';
import SubscribeForm from '../components/shared/SubscribeForm';
import MarginMateShowcase from '../components/shared/MarginMateShowcase';
import { tools } from '../data/mock';

const ProductPage = ({ slug }) => {
  const tool = tools.find((t) => t.id === slug);

  if (!tool) {
    return (
      <Layout>
        <SEO title="Product Not Found" description="The requested product was not found." />
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Product not found</h1>
            <Link to="/tools">
              <Button className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                View all tools
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const isLive = tool.status === 'live';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://summarygpt.com' },
      { '@type': 'ListItem', position: 2, name: 'Tools', item: 'https://summarygpt.com/tools' },
      { '@type': 'ListItem', position: 3, name: tool.name, item: `https://summarygpt.com/${tool.id}` },
    ],
  };

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: tool.name,
    description: tool.description,
    applicationCategory: 'ProductivityApplication',
    operatingSystem: 'Web',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: tool.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <Layout>
      <SEO
        title={tool.seoTitle || tool.name}
        description={tool.seoDescription || tool.description}
        path={`/${tool.id}`}
        schema={[breadcrumbSchema, productSchema, faqSchema]}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 sgpt-glow opacity-60 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors mb-10 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            All tools
          </Link>

          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
                {tool.name}
              </h1>
              {isLive ? (
                <span className="px-4 py-1.5 text-sm font-semibold bg-emerald-500/15 text-emerald-400 rounded-full border border-emerald-500/25">
                  Live
                </span>
              ) : (
                <span className="px-4 py-1.5 text-sm font-semibold bg-amber-500/15 text-amber-400 rounded-full border border-amber-500/25">
                  Coming Soon
                </span>
              )}
            </div>
            <p className="text-xl md:text-2xl text-slate-400 mb-10 leading-relaxed">
              {tool.description}
            </p>
            
            {isLive && tool.appUrl ? (
              <a href={tool.appUrl} target="_blank" rel="noopener noreferrer">
                <Button className="h-14 px-10 text-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 font-semibold shadow-xl shadow-amber-500/20 hover:shadow-amber-500/35 transition-all duration-300 border-0 gap-2.5 rounded-xl">
                  {tool.ctaLabel || `Try ${tool.name}`}
                  <ExternalLink className="w-5 h-5" />
                </Button>
              </a>
            ) : (
              <a href={`mailto:hello@summarygpt.com?subject=${tool.name}%20Early%20Access`}>
                <Button className="h-14 px-10 text-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 font-semibold shadow-xl shadow-amber-500/20 hover:shadow-amber-500/35 transition-all duration-300 border-0 gap-2.5 rounded-xl">
                  Get early access
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </a>
            )}

            {(tool.disclaimer || tool.privacyUrl) && (
              <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                {tool.disclaimer && <span>{tool.disclaimer}</span>}
                {tool.privacyUrl && (
                  <Link
                    to={tool.privacyUrl}
                    className="text-slate-400 hover:text-amber-400 underline underline-offset-4 transition-colors"
                  >
                    Privacy Policy
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* Answer Block (AEO) */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 sgpt-glow-subtle opacity-40 pointer-events-none" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-5">What it is</h2>
              <p className="text-slate-300 leading-relaxed">{tool.whatItIs}</p>
            </div>
            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-5">Who it's for</h2>
              <p className="text-slate-300 leading-relaxed">{tool.whoItsFor}</p>
            </div>
            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-5">What you get</h2>
              <ul className="space-y-3">
                {tool.whatYouGet.slice(0, 3).map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* Full Benefits */}
      <section className="relative py-20 md:py-28">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-14 text-center">What You Get</h2>
          <div className="max-w-3xl mx-auto grid gap-4">
            {tool.whatYouGet.map((item, i) => (
              <div key={i} className="flex items-start gap-5 p-6 rounded-xl glass-card">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-lg text-slate-300 pt-2">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* Example Output */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 sgpt-glow-subtle opacity-30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 text-center">
            {tool.id === 'marginmate' ? 'See It In Action' : 'Example Output'}
          </h2>
          <p className="text-slate-400 text-center mb-14 max-w-2xl mx-auto text-lg">
            {tool.id === 'marginmate'
              ? `Exactly how ${tool.name} looks on wholesale.lcbo.com.`
              : `See what ${tool.name} can generate.`}
          </p>

          {tool.id === 'marginmate' ? (
            <div className="max-w-3xl mx-auto">
              <MarginMateShowcase title={tool.exampleOutput.title} slides={tool.exampleOutput.slides} />
            </div>
          ) : (
          <div className="max-w-2xl mx-auto">
            <div className="rounded-2xl glass-elevated overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-4 bg-black/30 border-b border-white/5">
                <div className="w-3 h-3 rounded-full bg-red-500/60" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                <div className="w-3 h-3 rounded-full bg-green-500/60" />
                <span className="ml-4 text-sm font-medium text-slate-400">{tool.exampleOutput.title}</span>
              </div>

              <div className="p-6">
                {tool.id === 'epic' && (
                  <div className="space-y-3">
                    {tool.exampleOutput.phases.map((phase, i) => (
                      <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-black/20 border border-white/5">
                        <div>
                          <p className="font-medium text-white">{phase.name}</p>
                          <p className="text-sm text-slate-500">{phase.tasks} tasks</p>
                        </div>
                        <span className="text-amber-400 text-sm font-semibold">{phase.duration}</span>
                      </div>
                    ))}
                  </div>
                )}
                {tool.id === 'fork' && (
                  <div className="space-y-3">
                    {tool.exampleOutput.branches.map((branch, i) => (
                      <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-black/20 border border-white/5">
                        <p className="font-medium text-white">{branch.name}</p>
                        <span className="text-amber-400 text-sm font-semibold">{branch.insights} insights</span>
                      </div>
                    ))}
                  </div>
                )}
                {tool.id === 'snapstax' && (
                  <div className="space-y-3">
                    {tool.exampleOutput.items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-black/20 border border-white/5">
                        <div>
                          <p className="font-medium text-white">{item.vendor}</p>
                          <p className="text-sm text-slate-500">{item.category}</p>
                        </div>
                        <span className="text-emerald-400 font-semibold">{item.amount}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </section>

      {tool.roadmap && (
        <>
          {/* Section Divider */}
          <div className="section-divider mx-auto max-w-5xl" />

          {/* Roadmap */}
          <section className="relative py-20 md:py-28">
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 text-center">What's Next</h2>
              <p className="text-slate-400 text-center mb-14 max-w-2xl mx-auto text-lg">
                Built to grow with the store owners using it.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {tool.roadmap.map((item, i) => (
                  <div key={i} className="glass-card p-8 rounded-2xl">
                    <span className="inline-block px-3 py-1 mb-5 text-xs font-semibold bg-amber-500/15 text-amber-400 rounded-full border border-amber-500/25">
                      {item.tag}
                    </span>
                    <div className="text-2xl mb-4">{item.icon}</div>
                    <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                    <p className="text-slate-400 leading-relaxed">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* FAQ */}
      <section className="relative py-20 md:py-28">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-14 text-center">FAQ</h2>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {tool.faqs.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="rounded-xl glass-card px-6 border-white/5 data-[state=open]:border-amber-500/25 transition-colors duration-300"
                >
                  <AccordionTrigger className="text-left text-lg font-medium text-white hover:text-amber-400 transition-colors py-6 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400 pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* CTA */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 sgpt-glow opacity-40 pointer-events-none" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to try {tool.name}?
          </h2>
          <p className="text-xl text-slate-400 mb-12">{tool.tagline}</p>
          
          {isLive && tool.appUrl ? (
            <a href={tool.appUrl} target="_blank" rel="noopener noreferrer">
              <Button className="h-14 px-12 text-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 font-semibold shadow-xl shadow-amber-500/20 hover:shadow-amber-500/35 transition-all duration-300 border-0 gap-2.5 rounded-xl">
                {tool.ctaLabel || `Try ${tool.name}`}
                <ExternalLink className="w-5 h-5" />
              </Button>
            </a>
          ) : (
            <div className="max-w-md mx-auto">
              <SubscribeForm />
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default ProductPage;
